/**
 * CloakedWritesAudit — Maker-only forensic dashboard for cross-role writes.
 *
 * Lists every write that hit the backend with a valid `X-Impersonate-Role`
 * header (i.e. a cloaked Maker write). Populated by the
 * `middleware/cloak_audit.py` rolling log.
 *
 * Surface area:
 *   • Three top-stat cards (all-time total, last 24h, most-recent event).
 *   • Filter row (cloaked role, method, status class).
 *   • Summary chip row (counts per cloaked role, per status class) — driven
 *     by the same filter so the chips always reflect the visible rows.
 *   • Paginated table (most recent first) with method-colored pill,
 *     status badge, path, real Maker email, cloaked role, duration,
 *     IP and timestamp.
 */
import React, { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { Shield, AlertTriangle, Clock, Filter, RefreshCw } from 'lucide-react';

/* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the params-driven load effect. */

const ROLE_OPTIONS = [
  'holding_admin', 'chairman', 'ceo', 'cfo', 'legal',
  'investor', 'founder', 'startup_member', 'guest',
];
const METHODS = ['POST', 'PATCH', 'PUT', 'DELETE'];

const METHOD_COLORS = {
  POST: 'bg-emerald-500 text-emerald-50',
  PATCH: 'bg-sky-500 text-sky-50',
  PUT: 'bg-violet-500 text-violet-50',
  DELETE: 'bg-rose-500 text-rose-50',
};

const STATUS_COLORS = (s) =>
  s < 300 ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
  : s < 400 ? 'bg-sky-500/15 text-sky-300 border-sky-500/30'
  : s < 500 ? 'bg-amber-500/15 text-amber-300 border-amber-500/30'
  : 'bg-rose-500/15 text-rose-300 border-rose-500/30';

const STATUS_CLASS_COLORS = {
  '2xx': 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  '3xx': 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  '4xx': 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  '5xx': 'bg-rose-500/15 text-rose-300 border-rose-500/30',
};

function StatCard({ label, value, icon, sub }) {
  return (
    <div className="rounded-xl border border-white/10 bg-zinc-950/60 px-4 py-3 flex items-center gap-3">
      <div className="rounded-lg bg-amber-500/10 p-2 text-amber-400">{icon}</div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold">{label}</div>
        <div className="text-xl font-bold text-white mt-0.5">{value}</div>
        {sub && <div className="text-[10.5px] text-zinc-500 mt-0.5 truncate">{sub}</div>}
      </div>
    </div>
  );
}

function formatTs(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toISOString().replace('T', ' ').slice(0, 16) + ' UTC';
}

export default function CloakedWritesAudit() {
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ by_role: {}, by_status: {} });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter state
  const [cloakedRole, setCloakedRole] = useState('');
  const [method, setMethod] = useState('');
  const [statusClass, setStatusClass] = useState('');
  const [offset, setOffset] = useState(0);
  const limit = 25;

  const params = useMemo(() => {
    const p = { limit, offset };
    if (cloakedRole) p.cloaked_role = cloakedRole;
    if (method) p.method = method;
    if (statusClass === '2xx') { p.status_gte = 200; p.status_lt = 300; }
    if (statusClass === '4xx') { p.status_gte = 400; p.status_lt = 500; }
    if (statusClass === '5xx') { p.status_gte = 500; p.status_lt = 600; }
    return p;
  }, [cloakedRole, method, statusClass, offset]);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const [listResp, statsResp] = await Promise.all([
        api.get('/admin/audit/cloaked-writes', { params }),
        api.get('/admin/audit/cloaked-writes/stats'),
      ]);
      setRows(listResp.data?.rows || []);
      setSummary(listResp.data?.summary || { by_role: {}, by_status: {} });
      setTotal(listResp.data?.total || 0);
      setStats(statsResp.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [params]);

  const lastEventTxt = stats?.last_event
    ? `${(stats.last_event.cloaked_role || '?').toUpperCase()} · ${formatTs(stats.last_event.ts)}`
    : 'No events yet';

  return (
    <div className="bg-zinc-950 min-h-full text-white px-6 py-8 space-y-6" data-testid="cloaked-writes-audit">
      {/* Hero */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-amber-400 mb-1">
            <Shield size={11} className="inline -mt-0.5 mr-1" />
            Maker · Forensic record
          </div>
          <h1 className="text-3xl font-bold">Cloaked Writes Audit</h1>
          <p className="text-zinc-400 text-sm mt-2 max-w-2xl">
            Every write request that hit the backend while you were cloaked as another role.
            Captured by the <code className="text-[11px] font-mono px-1 py-0.5 rounded bg-zinc-800 text-amber-300">CloakAuditMiddleware</code>{' '}
            — append-only, ordered most-recent first, retained indefinitely for forensic review.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          data-testid="cloaked-writes-refresh"
          disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded border border-zinc-700 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 text-sm transition-colors disabled:opacity-50"
        >
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatCard label="Total ever" value={stats?.total_ever ?? '—'} icon={<Shield size={16} />} />
        <StatCard label="Last 24 hours" value={stats?.last_24h ?? '—'} icon={<Clock size={16} />} />
        <StatCard label="Most recent" value={lastEventTxt} icon={<AlertTriangle size={16} />} sub={stats?.last_event?.ts?.slice(0, 19)} />
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-white/10 bg-zinc-900/40 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter size={13} className="text-zinc-500" />
          <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold">Filters</span>
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <label className="flex items-center gap-2 text-[12px] text-zinc-300">
            Cloaked role
            <select
              data-testid="filter-cloaked-role"
              value={cloakedRole}
              onChange={(e) => { setCloakedRole(e.target.value); setOffset(0); }}
              className="bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-xs text-white"
            >
              <option value="">All</option>
              {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
          <label className="flex items-center gap-2 text-[12px] text-zinc-300">
            Method
            <select
              data-testid="filter-method"
              value={method}
              onChange={(e) => { setMethod(e.target.value); setOffset(0); }}
              className="bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-xs text-white"
            >
              <option value="">All</option>
              {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          <label className="flex items-center gap-2 text-[12px] text-zinc-300">
            Status
            <select
              data-testid="filter-status-class"
              value={statusClass}
              onChange={(e) => { setStatusClass(e.target.value); setOffset(0); }}
              className="bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-xs text-white"
            >
              <option value="">All</option>
              <option value="2xx">2xx success</option>
              <option value="4xx">4xx client error</option>
              <option value="5xx">5xx server error</option>
            </select>
          </label>
          {(cloakedRole || method || statusClass) && (
            <button
              type="button"
              onClick={() => { setCloakedRole(''); setMethod(''); setStatusClass(''); setOffset(0); }}
              className="text-[11px] text-amber-400 hover:text-amber-300 underline"
            >
              Clear all
            </button>
          )}
        </div>

        {/* Summary chips */}
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(summary.by_status || {}).map(([cls, n]) => (
            <span
              key={cls}
              className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded border ${STATUS_CLASS_COLORS[cls]}`}
              data-testid={`summary-status-${cls}`}
            >
              {cls}: {n}
            </span>
          ))}
          {Object.entries(summary.by_role || {}).map(([role, classes]) => {
            const tot = Object.values(classes).reduce((a, b) => a + b, 0);
            return (
              <span
                key={role}
                className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded border border-amber-500/30 bg-amber-500/10 text-amber-300"
                data-testid={`summary-role-${role}`}
              >
                {role}: {tot}
              </span>
            );
          })}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-white/10 bg-zinc-950/40 overflow-hidden">
        {error && (
          <div className="px-4 py-3 bg-rose-950/40 text-rose-300 text-sm border-b border-rose-500/30">
            Error: {error}
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]" data-testid="cloaked-writes-table">
            <thead className="bg-zinc-900/60 text-zinc-400 text-[10px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2.5 font-bold">When</th>
                <th className="text-left px-3 py-2.5 font-bold">Method</th>
                <th className="text-left px-3 py-2.5 font-bold">Path</th>
                <th className="text-left px-3 py-2.5 font-bold">Cloaked as</th>
                <th className="text-left px-3 py-2.5 font-bold">Real Maker</th>
                <th className="text-left px-3 py-2.5 font-bold">Status</th>
                <th className="text-right px-3 py-2.5 font-bold">Duration</th>
                <th className="text-left px-3 py-2.5 font-bold">IP</th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-zinc-500">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-zinc-500">
                  No cloaked writes match these filters yet.
                </td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-white/5 hover:bg-zinc-900/30">
                  <td className="px-3 py-2 text-zinc-400 whitespace-nowrap" title={r.ts}>{formatTs(r.ts)}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block px-1.5 py-0.5 rounded text-[9.5px] font-bold ${METHOD_COLORS[r.method] || 'bg-zinc-700 text-white'}`}>
                      {r.method}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono text-white max-w-md truncate" title={r.path}>{r.path}</td>
                  <td className="px-3 py-2">
                    <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-500/15 text-amber-300 border border-amber-500/30">
                      {(r.cloaked_role || '?').toUpperCase()}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-zinc-400 truncate max-w-[200px]" title={r.real_email}>{r.real_email}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold border ${STATUS_COLORS(r.status)}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right text-zinc-400 tabular-nums">{r.duration_ms}ms</td>
                  <td className="px-3 py-2 font-mono text-[10.5px] text-zinc-500">{r.ip || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {total > limit && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-white/5 text-[11.5px] text-zinc-400">
            <div>
              Showing {offset + 1}–{Math.min(offset + rows.length, total)} of {total}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setOffset(Math.max(0, offset - limit))}
                disabled={offset === 0}
                className="px-2 py-1 rounded border border-zinc-700 hover:bg-zinc-800 disabled:opacity-40"
                data-testid="page-prev"
              >Prev</button>
              <button
                type="button"
                onClick={() => setOffset(offset + limit)}
                disabled={offset + limit >= total}
                className="px-2 py-1 rounded border border-zinc-700 hover:bg-zinc-800 disabled:opacity-40"
                data-testid="page-next"
              >Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
